import { useEffect, useCallback, useState, useMemo } from 'react';
import config from '@root/src/config';
import { realtimeManager } from '@services/websocket/RealtimeManager';
import { useCurrentUser } from '@features/user/hooks/useAuth';

type RoomType = 'conversation' | 'forum' | 'sst';
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export const useRealtime = () => {
  const { data: currentUser, isLoading } = useCurrentUser();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastError, setLastError] = useState<Error | null>(null);

  // Intentionally excludes isFetching — background refetches must not trigger a disconnect
  const isAuthenticated = !isLoading && !!currentUser;

  // Connect when authenticated (connect() is idempotent across instances). Closing the
  // socket is not this instance's call: useRealtimeSession does it once, on logout.
  useEffect(() => {
    if (isAuthenticated && config.features.websocket) {
      setConnectionState('connecting');
      setLastError(null);

      realtimeManager
        .connect()
        .then(() => setConnectionState('connected'))
        .catch((error: unknown) => {
          setConnectionState('error');
          setLastError(error instanceof Error ? error : new Error(String(error)));
          console.error('WebSocket: connection failed:', error);
        });
    } else if (!isAuthenticated) {
      setConnectionState('disconnected');
      setLastError(null);
    }
  }, [isAuthenticated]);

  // Keep connection state in sync with socket events
  useEffect(() => {
    return realtimeManager.onConnectionStateChange(connected => {
      setConnectionState(connected ? 'connected' : 'disconnected');
      if (connected) setLastError(null);
    });
  }, []);

  const joinRoom = useCallback((roomType: RoomType, roomId: string) => {
    realtimeManager.joinRoom(roomType, roomId);
  }, []);

  const leaveRoom = useCallback((roomType: RoomType, roomId: string) => {
    realtimeManager.leaveRoom(roomType, roomId);
  }, []);

  // Returns a cleanup function — pass directly to useEffect return
  const on = useCallback(<T = unknown>(event: string, callback: (data: T) => void) => {
    return realtimeManager.on(event, callback);
  }, []);

  const startTyping = useCallback((conversationId: string) => {
    realtimeManager.startTyping(conversationId);
  }, []);

  const stopTyping = useCallback((conversationId: string) => {
    realtimeManager.stopTyping(conversationId);
  }, []);

  return useMemo(
    () => ({
      joinRoom,
      leaveRoom,
      on,
      startTyping,
      stopTyping,
      isConnected: connectionState === 'connected',
      connectionState,
      lastError,
    }),
    [joinRoom, leaveRoom, on, startTyping, stopTyping, connectionState, lastError],
  );
};
